import React, { useState } from 'react';
import { Modal, Form, Input, Select, Button, Upload, message, Radio } from 'antd';
import { UploadOutlined, InboxOutlined } from '@ant-design/icons';
import api from '../../utils/api';

const { Option } = Select;
const { Dragger } = Upload;

const DOC_TITLES = ['TDS', 'COA', 'SDS', '包装照', '产品照', '其他'];

export default function GradeDocumentAttacher({ visible, onCancel, gradeId, gradeName, onSuccess }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [selectedTitle, setSelectedTitle] = useState(DOC_TITLES[0]);

    const handleTitleChange = (value) => {
        setSelectedTitle(value);
        if (value !== '其他') {
            form.setFieldsValue({ titleInput: value });
        } else {
            form.setFieldsValue({ titleInput: '' });
        }
    };

    const determineFileType = (file) => {
        const type = file.type;
        const name = file.name.toLowerCase();
        if (type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/.test(name)) {
            return 1; // Image
        }
        if (type === 'application/pdf' || /\.pdf$/.test(name)) {
            return 2; // PDF
        }
        return 0; // Other
    };

    const customRequest = async ({ file, onSuccess: onUploadSuccess, onError }) => {
        const formData = new FormData();
        formData.append('files', file);
        
        // Get the actual title to use for upload
        const titleValue = form.getFieldValue('titleInput') || selectedTitle;
        
        try {
            setUploading(true);
            
            // Use URLSearchParams to ensure proper encoding of special characters like [] in allowTypes
            // This prevents RFC 7230/3986 errors on Tomcat
            const params = new URLSearchParams();
            params.append('title', titleValue);
            params.append('uploadMark', 'grade_document');
            params.append('maxCount', '1');
            params.append('maxSize', String(1.5 * 1024 * 1024));
            params.append('allowTypes', '["jpg","jpeg","png","gif","webp","pdf","doc","docx","xls","xlsx","ppt","pptx","txt"]');
            params.append('isStamp', '1');

            const res = await api.post('/uploads/odm', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                params: params
            });
            
            if (res && res.code === 200 && res.data && res.data.length > 0) {
                const url = res.data[0].url;
                onUploadSuccess(url);
                message.success('文件上传成功');
                // Store the uploaded file info
                setFileList([{
                    uid: file.uid,
                    name: file.name,
                    status: 'done',
                    url: url,
                    type: determineFileType(file)
                }]);
            } else {
                onError(new Error('Upload failed'));
                message.error('文件上传失败');
            }
        } catch (e) {
            console.error(e);
            onError(e);
            message.error('文件上传出错');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (fileList.length === 0) {
                message.error('请上传文件');
                return;
            }

            const uploadedFile = fileList[0];
            const finalTitle = values.titleSelect === '其他' ? values.titleInput : values.titleSelect;

            const payload = {
                gradeId: gradeId,
                uri: uploadedFile.url,
                title: finalTitle,
                type: uploadedFile.type
            };

            setLoading(true);
            const res = await api.post('/approvals/submit', {
                type: 'ATTACH_GRADE_DOCUMENT',
                remark: `添附文档: ${gradeName}`,
                payload: JSON.stringify(payload)
            });

            if (res && res.code === 200) {
                message.success('申请提交成功，请等待审批');
                if (onSuccess) onSuccess();
                onCancel();
                form.resetFields();
                setFileList([]);
                setSelectedTitle(DOC_TITLES[0]);
            } else {
                message.error(res.message || '提交失败');
            }
        } catch (e) {
            console.error(e);
            message.error('提交出错');
        } finally {
            setLoading(false);
        }
    };

    const normFile = (e) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e && e.fileList;
    };

    const handleCancel = () => {
        onCancel();
        form.resetFields();
        setFileList([]);
        setSelectedTitle(DOC_TITLES[0]);
    }

    return (
        <Modal
            title="添附本公司文档"
            open={visible}
            onCancel={handleCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            okText="提交申请"
            cancelText="取消"
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    titleSelect: DOC_TITLES[0],
                    titleInput: DOC_TITLES[0]
                }}
            >
                <Form.Item label="文档类型" required style={{ marginBottom: 0 }}>
                    <Form.Item
                        name="titleSelect"
                        rules={[{ required: true, message: '请选择文档类型' }]}
                        style={{ display: 'inline-block', width: 'calc(40% - 8px)' }}
                    >
                        <Select onChange={handleTitleChange}>
                            {DOC_TITLES.map(title => (
                                <Option key={title} value={title}>{title}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="titleInput"
                        rules={[{ required: true, message: '请输入文档名称' }]}
                        style={{ display: 'inline-block', width: '60%', marginLeft: '8px' }}
                    >
                        <Input disabled={selectedTitle !== '其他'} placeholder="文档名称" />
                    </Form.Item>
                </Form.Item>

                <Form.Item
                    label="上传文档"
                    name="file"
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    rules={[{ required: true, message: '请上传文件' }]}
                >
                    <Dragger
                        customRequest={customRequest}
                        maxCount={1}
                        fileList={fileList}
                        onRemove={() => setFileList([])}
                        beforeUpload={(file) => {
                            const isLt15M = file.size / 1024 / 1024 < 1.5;
                            if (!isLt15M) {
                                message.error('文件大小不能超过 1.5MB!');
                            }
                            return isLt15M;
                        }}
                        showUploadList={{ showRemoveIcon: true }}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">点击或拖拽文件到此处上传</p>
                        <p className="ant-upload-hint">
                            支持图片、PDF、Office文档等，最大1.5MB
                        </p>
                    </Dragger>
                </Form.Item>
            </Form>
        </Modal>
    );
}
